import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Home } from "lucide-react";
import Feedbacks from "./feedbacks";
import News from "./news";
import ClientProfile from "./profile";
import ThemeSelector from "../components/ThemeSelector";
import NetworkLogoBadge from "../components/NetworkLogoBadge";
import ClientChat from "./chat";
import ClientCars from "./cars";
import ClientOperatorInfo from "./operator-info";
import ClientPolls from "./polls";
import AboutClients from "./about";
import DoorAccess from "./device-access";
import CameraAccess from "./camera-access";
import ClientSensors from "./sensors";
import ClientPortal from "./portal";
import ClientWidgets from "./widgets";
import ClientRequests from "./requests";
import { listClientRequests } from "../services/requests";
import { fetchNews, fetchUnreadNews } from "../services/news";
import { fetchUnreadOperatorInfo } from "../services/operator_info";
import { fetchUnreadClientPolls } from "../services/polls";
import { fetchSockets } from "../services/device-access";
import { fetchClientCameras } from "../services/camera-access";
import { fetchClientSensorAccess } from "../services/sensors";
import { fetchClient, acceptClientTerms, fetchClientAccessOverview, dismissRemovedNetworkBanner } from "../services/profile";
import { getMe, logout } from "../services/session";
import { fetchClientPortalSettings } from "../services/client_portal";
import { fetchClientNetworkFeatures } from "../services/network-features";
import { fetchLicenseStatus } from "../services/license";
import { getToMeConversations } from "../services/chat";
import { useWebSocket } from "../context/WebSocketContext";
import { apiFetch } from "../utils/apiFetch";
import { setNativeAppBadge } from "../utils/nativeBadge";
import { loadWeatherTextForRibbon } from "../utils/weather";
import { pushAndroidBackHandler } from "../utils/androidBack";
import { notifyInAppSound, withInAppSound } from "../utils/notificationSound";
import { API_BASE_CLIENT } from "../config/vars";

function readPendingPushSection(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const kind = String(parsed?.kind || "").trim();
        if (!kind) return null;

        if (kind === "news") return "news";
        if (kind === "chat") return "chat";
        if (kind === "operator_info") return "operator-info";
        if (kind === "poll") return "polls";
        if (kind === "sensor_alert") return "sensors";
        if (["request", "request_response", "request_reply"].includes(kind)) return "requests";
        if (["feedback", "feedback_response", "feedback_reply"].includes(kind)) return "feedbacks";
        if (["access_request", "client_access_request"].includes(kind)) return "profile";
    } catch (_) {
        // ignore
    }
    return null;
}

function storePendingItemId(storageKey, value) {
    const id = parseInt(value);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
        localStorage.setItem(storageKey, String(id));
    } catch (_) {
        // ignore
    }
}

function applyPendingPushItemIds(parsed) {
    const kind = String(parsed?.kind || "").trim();
    if (!kind) return;

    if (kind === "chat") storePendingItemId("pendingChatOpenHeadId_client", parsed?.head_id);
    else if (kind === "news") storePendingItemId("pendingNewsOpenId_client", parsed?.news_id);
    else if (kind === "operator_info") storePendingItemId("pendingOperatorInfoOpenId_client", parsed?.info_id);
    else if (kind === "poll") storePendingItemId("pendingPollOpenId_client", parsed?.poll_id);
    else if (["request", "request_response", "request_reply"].includes(kind)) {
        storePendingItemId("pendingRequestOpenId_client", parsed?.request_id);
    } else if (["feedback", "feedback_response", "feedback_reply"].includes(kind)) {
        storePendingItemId("pendingFeedbackOpenId_client", parsed?.feedback_id);
    } else if (kind === "sensor_alert") {
        storePendingItemId("pendingSensorOpenId_client", parsed?.sensor_id);
        storePendingItemId("pendingSensorEventOpenId_client", parsed?.event_id);
    } else if (["access_request", "client_access_request"].includes(kind)) {
        storePendingItemId("pendingClientAccessRequestOpenId_client", parsed?.access_request_id || parsed?.request_id);
    }
}

const ClientPage = () => {
    const normalizeIdList = (values) => Array.from(new Set(
        (Array.isArray(values) ? values : [])
            .map((value) => parseInt(value))
            .filter((value) => Number.isFinite(value) && value > 0)
    ));

    const [selectedSection, setSelectedSection] = useState(() => {
        const forced = localStorage.getItem("forcePasswordChange") === "true";
        if (forced) return "profile";
        return readPendingPushSection("WR_PENDING_PUSH_OPEN") || "dashboard";
    });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuMounted, setMenuMounted] = useState(false);
    const [openGroupId, setOpenGroupId] = useState(null);
    const [clientName, setClientName] = useState("");
    const [hasUnreadNews, setHasUnreadNews] = useState(false);
    const [unreadNewsCount, setUnreadNewsCount] = useState(0);
    const [hasUnreadFeedback, setHasUnreadFeedback] = useState(false);
    const [hasUnreadPolls, setHasUnreadPolls] = useState(false);
    const [unreadPollsCount, setUnreadPollsCount] = useState(0);
    const [hasOperatorInfo, setHasOperatorInfo] = useState(false);
    const [unreadOperatorInfoCount, setUnreadOperatorInfoCount] = useState(0);
    const [hasChats, setHasChats] = useState(false);
    const [unreadChatsCount, setUnreadChatsCount] = useState(0);
    const [hasRequestUpdates, setHasRequestUpdates] = useState(false);
    const [hasAccessRequestUpdates, setHasAccessRequestUpdates] = useState(false);
    const [menuModel, setMenuModel] = useState({ topItems: [], groups: [], bottomItems: [] });
    const [hasDoorAccess, setHasDoorAccess] = useState(false);
    const [hasCameraAccess, setHasCameraAccess] = useState(false);
    const [forcePasswordChange, setForcePasswordChange] = useState(() => localStorage.getItem("forcePasswordChange") === "true");
    const [highlightedFeedbacks, setHighlightedFeedbacks] = useState(new Set());
    const [serverOffsetMs, setServerOffsetMs] = useState(null);
    const [serverTick, setServerTick] = useState(0);
    const [showTime, setShowTime] = useState(() => window.innerWidth >= 520);
    const [weatherText, setWeatherText] = useState(null);
    const [chatShellNav, setChatShellNav] = useState(null);
    const selectedSectionRef = useRef(selectedSection);
    const [portalSettings, setPortalSettings] = useState(null);
    const [networkFeatures, setNetworkFeatures] = useState(null); // null = not loaded; standalone / fail-open treated as all on
    const [licenseStatus, setLicenseStatus] = useState(null);
    const [licenseReady, setLicenseReady] = useState(false);
    const [clientScope, setClientScope] = useState({ networkIds: [], locationIds: [] });
    const [clientScopeReady, setClientScopeReady] = useState(false);
    const [removedNetworkBanner, setRemovedNetworkBanner] = useState(null);
    const [sensorAccess, setSensorAccess] = useState({
        page_enabled: false,
        can_manage_private: false,
        can_view_public: false,
        public_sensor_count: 0,
        standalone_mode: false,
    });

    const navbarRef = useRef(null);
    const drawerRef = useRef(null);

    const [termsRequired, setTermsRequired] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);
    const [termsSubmitting, setTermsSubmitting] = useState(false);
    const [termsError, setTermsError] = useState("");

    const navigate = useNavigate();
    const { connect, subscribe, unsubscribe } = useWebSocket();

    const activeNetworkId = parseInt(localStorage.getItem("networkId"));
    const clientId = parseInt(localStorage.getItem("clientId"));
    const activeLocationId = parseInt(localStorage.getItem("locationId"));
    const networkIds = normalizeIdList([
        ...clientScope.networkIds,
        Number.isFinite(activeNetworkId) && activeNetworkId > 0 ? activeNetworkId : null,
    ]);
    const locationIds = normalizeIdList([
        ...clientScope.locationIds,
        Number.isFinite(activeLocationId) && activeLocationId > 0 ? activeLocationId : null,
    ]);
    const hasClientAccess = networkIds.length > 0 || locationIds.length > 0;
    const standaloneClient = clientScopeReady && !hasClientAccess;
    const hasLocationAccess = locationIds.length > 0;
    const hasNetworkAccess = networkIds.length > 0;
    const feedbacksEnabled = portalSettings?.show_feedbacks !== false;
    const clientAccessViewedStorageKey = clientId ? `lastViewedClientAccessAt:${clientId}` : "lastViewedClientAccessAt";
    const clientRequestsViewedStorageKey = clientId ? `lastViewedClientRequestsAt:${clientId}` : "lastViewedClientRequestsAt";

    const networkModuleOn = useCallback((featureKey) => {
        if (standaloneClient || networkFeatures === null) return true;
        return networkFeatures[featureKey] !== false;
    }, [standaloneClient, networkFeatures]);

    const enabledModules = useMemo(() => {
        const p = portalSettings || {};
        return {
            billing: networkModuleOn("billing"),
            info: p.show_operator_info !== false && networkModuleOn("operator_info"),
            news: p.show_news !== false && networkModuleOn("news"),
            polls: p.show_polls !== false && networkModuleOn("polls"),
            chats: p.show_chat !== false && networkModuleOn("chat"),
            requests: p.show_requests !== false && networkModuleOn("requests"),
            sensors: networkModuleOn("sensors"),
            door: p.show_door_access !== false && networkModuleOn("door_access"),
            camera: p.show_camera_access !== false && networkModuleOn("camera_access"),
            cars: p.show_cars !== false && networkModuleOn("cars"),
            feedbacks: p.show_feedbacks !== false && networkModuleOn("feedbacks"),
        };
    }, [portalSettings, networkModuleOn]);

    const requestsEnabled = enabledModules.requests;
    const pollsEnabled = hasClientAccess && enabledModules.polls;
    const carsEnabled = hasLocationAccess && hasNetworkAccess && enabledModules.cars;

    const chatEnabled = hasClientAccess && enabledModules.chats;
    const operatorInfoEnabled = hasClientAccess && enabledModules.info;

    const isLicenseInvalid = licenseReady && licenseStatus && licenseStatus.valid === false;
    const warningDaysLeft = (licenseStatus && licenseStatus.valid && Number.isFinite(licenseStatus.days_left))
        ? licenseStatus.days_left
        : null;
    const showLicenseWarning = !isLicenseInvalid && warningDaysLeft !== null && warningDaysLeft <= 15;

    const getMenuBadgeCount = useCallback((key) => {
        if (key === "news") return unreadNewsCount;
        if (key === "polls") return unreadPollsCount;
        if (key === "operator-info") return unreadOperatorInfoCount;
        if (key === "chat") return unreadChatsCount;
        return 0;
    }, [unreadNewsCount, unreadPollsCount, unreadOperatorInfoCount, unreadChatsCount]);

    const toggleMenu = useCallback(() => {
        startTransition(() => {
            setMenuOpen((open) => {
                const next = !open;
                if (next) setMenuMounted(true);
                return next;
            });
        });
    }, []);

    const closeMenu = useCallback(() => {
        startTransition(() => setMenuOpen(false));
    }, []);

    useEffect(() => {
        const loadClientScope = async () => {
            try {
                const overview = await fetchClientAccessOverview();
                const approvedContexts = Array.isArray(overview?.approved_contexts) ? overview.approved_contexts : [];
                setClientScope({
                    networkIds: normalizeIdList(approvedContexts.map((item) => item?.network_id)),
                    locationIds: normalizeIdList(approvedContexts.map((item) => item?.location_id)),
                });
                if (overview?.show_removed_banner) {
                    setRemovedNetworkBanner({
                        networkName: overview?.last_removed_network_name || "мережі",
                    });
                } else {
                    setRemovedNetworkBanner(null);
                }
            } catch (_) {
                setClientScope({
                    networkIds: normalizeIdList([activeNetworkId]),
                    locationIds: normalizeIdList([activeLocationId]),
                });
            } finally {
                setClientScopeReady(true);
            }
        };
        loadClientScope();
    }, [activeLocationId, activeNetworkId]);

    useEffect(() => {
        const loadSensorAccess = async () => {
            if (!clientId) {
                setSensorAccess({
                    page_enabled: false,
                    can_manage_private: false,
                    can_view_public: false,
                    public_sensor_count: 0,
                    standalone_mode: false,
                });
                return;
            }

            try {
                const access = await fetchClientSensorAccess();
                setSensorAccess({
                    page_enabled: access?.page_enabled === true,
                    can_manage_private: access?.page_enabled === true || access?.can_manage_private === true,
                    can_view_public: access?.can_view_public === true,
                    public_sensor_count: Number(access?.public_sensor_count) || 0,
                    standalone_mode: access?.standalone_mode === true,
                });
            } catch (_) {
                setSensorAccess({
                    page_enabled: true,
                    can_manage_private: true,
                    can_view_public: false,
                    public_sensor_count: 0,
                    standalone_mode: false,
                });
            }
        };

        loadSensorAccess();
    }, [clientId, clientScope]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                setMenuOpen(false);
                setOpenGroupId(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!openGroupId) return;
        const onDocMouseDown = (e) => {
            const target = e.target;
            if (navbarRef.current && navbarRef.current.contains(target)) return;
            if (drawerRef.current && drawerRef.current.contains(target)) return;
            setOpenGroupId(null);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [openGroupId]);

    // Native (Capacitor) app icon badge: best-effort.
    // Note: this updates only while the app is running (foreground/background).
    useEffect(() => {
        const count =
            Math.max(0, unreadNewsCount) +
            (pollsEnabled ? Math.max(0, unreadPollsCount) : 0) +
            (chatEnabled ? Math.max(0, unreadChatsCount) : 0) +
            (operatorInfoEnabled ? Math.max(0, unreadOperatorInfoCount) : 0) +
            (feedbacksEnabled && hasUnreadFeedback ? 1 : 0) +
            (hasRequestUpdates ? 1 : 0) +
            (hasAccessRequestUpdates ? 1 : 0);
        setNativeAppBadge(count);
    }, [unreadNewsCount, pollsEnabled, unreadPollsCount, chatEnabled, unreadChatsCount, operatorInfoEnabled, unreadOperatorInfoCount, feedbacksEnabled, hasUnreadFeedback, hasRequestUpdates, hasAccessRequestUpdates]);

    const refreshUnreadNews = useCallback(async () => {
        if (!hasClientAccess) return;
        try {
            const unread = await fetchUnreadNews();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadNewsCount(count);
            if (unread?.has_unread === true || count > 0) setHasUnreadNews(true);
            else if (unread?.has_unread === false || count === 0) setHasUnreadNews(false);
        } catch (_) {
            // If the check fails, keep current badge state (avoid false positives).
        }
    }, [hasClientAccess]);

    const refreshUnreadPolls = useCallback(async () => {
        if (!hasClientAccess) return;
        if (!pollsEnabled) return;
        try {
            const unread = await fetchUnreadClientPolls();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadPollsCount(count);
            if (unread?.has_unread === true || count > 0) setHasUnreadPolls(true);
            else if (unread?.has_unread === false || count === 0) setHasUnreadPolls(false);
        } catch (_) {
            // keep current state
        }
    }, [hasClientAccess, pollsEnabled]);

    const refreshUnreadChats = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;
        if (!chatEnabled) return;

        try {
            const convs = await getToMeConversations();
            if (Array.isArray(convs)) {
                const unreadCount = convs.reduce(
                    (sum, conversation) => sum + Math.max(0, Number(conversation?.unread_count) || 0),
                    0
                );
                setUnreadChatsCount(unreadCount);
                setHasChats(unreadCount > 0);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, clientId, chatEnabled]);

    const markClientAccessUpdatesViewed = useCallback(() => {
        try {
            localStorage.setItem(clientAccessViewedStorageKey, new Date().toISOString());
        } catch (_) {
            // ignore
        }
        setHasAccessRequestUpdates(false);
    }, [clientAccessViewedStorageKey]);

    const markClientRequestUpdatesViewed = useCallback(() => {
        try {
            localStorage.setItem(clientRequestsViewedStorageKey, new Date().toISOString());
        } catch (_) {
            // ignore
        }
        setHasRequestUpdates(false);
    }, [clientRequestsViewedStorageKey]);

    const refreshClientAccessUpdates = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;

        try {
            let hasStoredMarker = false;
            let lastViewedTs = 0;
            try {
                const raw = localStorage.getItem(clientAccessViewedStorageKey);
                hasStoredMarker = Boolean(raw);
                const parsed = raw ? Date.parse(raw) : 0;
                lastViewedTs = Number.isFinite(parsed) ? parsed : 0;
            } catch (_) {
                hasStoredMarker = false;
                lastViewedTs = 0;
            }

            if (!hasStoredMarker) {
                try {
                    localStorage.setItem(clientAccessViewedStorageKey, new Date().toISOString());
                } catch (_) {
                    // ignore
                }
                setHasAccessRequestUpdates(false);
                return;
            }

            const overview = await fetchClientAccessOverview();
            const requests = Array.isArray(overview?.requests) ? overview.requests : [];

            const parseTime = (value) => {
                const ts = value ? Date.parse(value) : NaN;
                return Number.isFinite(ts) ? ts : 0;
            };

            const hasRequestUpdate = requests.some((request) => {
                const reviewedTs = request?.status && request.status !== "pending"
                    ? parseTime(request?.reviewed_at)
                    : 0;
                const operatorMessageTs = Array.isArray(request?.messages)
                    ? request.messages.reduce((latest, item) => {
                        if (item?.sender_type !== "operator") return latest;
                        return Math.max(latest, parseTime(item?.created_at));
                    }, 0)
                    : 0;
                return reviewedTs > lastViewedTs || operatorMessageTs > lastViewedTs;
            });

            setHasAccessRequestUpdates(hasRequestUpdate);
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, clientId, clientAccessViewedStorageKey]);

    const refreshClientRequestUpdates = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;
        if (!requestsEnabled) {
            setHasRequestUpdates(false);
            return;
        }

        try {
            let hasStoredMarker = false;
            let lastViewedTs = 0;
            try {
                const raw = localStorage.getItem(clientRequestsViewedStorageKey);
                hasStoredMarker = Boolean(raw);
                const parsed = raw ? Date.parse(raw) : 0;
                lastViewedTs = Number.isFinite(parsed) ? parsed : 0;
            } catch (_) {
                hasStoredMarker = false;
                lastViewedTs = 0;
            }

            if (!hasStoredMarker) {
                try {
                    localStorage.setItem(clientRequestsViewedStorageKey, new Date().toISOString());
                } catch (_) {
                    // ignore
                }
                setHasRequestUpdates(false);
                return;
            }

            const requests = await listClientRequests();
            const hasUpdate = (Array.isArray(requests) ? requests : []).some((request) => {
                if ((request?.status || "open") !== "closed") return false;
                const updatedTs = request?.updated_at ? Date.parse(request.updated_at) : NaN;
                return Number.isFinite(updatedTs) && updatedTs > lastViewedTs;
            });

            setHasRequestUpdates(hasUpdate);
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, clientId, requestsEnabled, clientRequestsViewedStorageKey]);

    const refreshOperatorInfoBadge = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!operatorInfoEnabled) return;

        try {
            const unread = await fetchUnreadOperatorInfo();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadOperatorInfoCount(count);
            if (unread?.has_unread === true || count > 0) setHasOperatorInfo(true);
            else if (unread?.has_unread === false || count === 0) setHasOperatorInfo(false);
            return;
        } catch (_) {
            // fall back to list-based heuristic
        }

        try {
            const res = await apiFetch(`${API_BASE_CLIENT}/operator-info/client`);
            if (!res.ok) return;
            const data = await res.json();
            if (!Array.isArray(data) || data.length === 0) return;

            const newestAt = data
                .map((x) => (x?.dtlm ? new Date(x.dtlm).getTime() : null))
                .filter((t) => typeof t === "number" && !Number.isNaN(t))
                .sort((a, b) => b - a)[0];

            if (!newestAt) return;

            const marker = localStorage.getItem("lastViewedOperatorInfoAt");
            if (!marker) {
                setUnreadOperatorInfoCount(Array.isArray(data) ? data.length : 0);
                setHasOperatorInfo(true);
                return;
            }

            const seenAt = new Date(marker).getTime();
            if (!Number.isNaN(seenAt) && newestAt > seenAt) {
                const count = data.filter((item) => {
                    const itemTs = item?.dtlm ? new Date(item.dtlm).getTime() : NaN;
                    return Number.isFinite(itemTs) && itemTs > seenAt;
                }).length;
                setUnreadOperatorInfoCount(count);
                setHasOperatorInfo(true);
            } else {
                setUnreadOperatorInfoCount(0);
                setHasOperatorInfo(false);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, operatorInfoEnabled]);

    // When app returns from background, refresh unread indicators.
    useEffect(() => {
        let lastRun = 0;

        const run = async () => {
            const now = Date.now();
            if (now - lastRun < 1000) return;
            lastRun = now;
            await refreshUnreadNews();
            await refreshUnreadPolls();
            await refreshOperatorInfoBadge();
            await refreshUnreadChats();
            await refreshClientRequestUpdates();
            await refreshClientAccessUpdates();
        };

        const onVis = () => {
            if (document.visibilityState === "visible") {
                run();
            }
        };

        document.addEventListener("visibilitychange", onVis);

        // Capacitor runtime: also listen to appStateChange if available.
        let removeAppListener = null;
        try {
            const capApp = window?.Capacitor?.Plugins?.App;
            if (capApp?.addListener) {
                const h = capApp.addListener("appStateChange", (st) => {
                    if (st?.isActive) run();
                });
                removeAppListener = () => {
                    try {
                        h?.remove?.();
                    } catch (_) {
                        // ignore
                    }
                };
            }
        } catch (_) {
            // ignore
        }

        // Foreground push: update dots immediately.
        const onPush = (e) => {
            const d = e?.detail || {};
            const kind = String(d.kind || "").trim();
            if (["request", "request_response", "request_reply"].includes(kind)) setHasRequestUpdates(true);
            if (["access_request", "client_access_request"].includes(kind)) setHasAccessRequestUpdates(true);
            run();
        };
        window.addEventListener("wrPushReceived", onPush);

        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("wrPushReceived", onPush);
            if (removeAppListener) removeAppListener();
        };
    }, [refreshUnreadNews, refreshUnreadPolls, refreshOperatorInfoBadge, refreshUnreadChats, refreshClientRequestUpdates, refreshClientAccessUpdates]);

    // ✅ Set client name from localStorage
    useEffect(() => {
        const storedName = localStorage.getItem("clientName");
        if (storedName) setClientName(storedName);
    }, []);

    // Load server time (from /login/me) for greeting date/time display
    useEffect(() => {
        const loadServerTime = async () => {
            try {
                const me = await getMe();
                if (!me) return;
                const serverMs = Date.parse(me.server_time_utc);
                if (!Number.isNaN(serverMs)) {
                    setServerOffsetMs(serverMs - Date.now());
                }
            } catch (_) {
                // ignore
            }
        };
        loadServerTime();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const text = await loadWeatherTextForRibbon();
                if (!cancelled) setWeatherText(text);
            } catch (_) {
                if (!cancelled) setWeatherText(null);
            }
        };

        load();

        const handler = () => load();
        window.addEventListener("wrWeatherChanged", handler);
        return () => {
            cancelled = true;
            window.removeEventListener("wrWeatherChanged", handler);
        };
    }, []);

    // Load license status early so we can show an informative banner/screen.
    useEffect(() => {
        const loadLicense = async () => {
            try {
                const ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
            } catch (_) {
                // If status can't be loaded, don't block UI immediately.
                setLicenseStatus(null);
            } finally {
                setLicenseReady(true);
            }
        };
        loadLicense();
    }, []);

    // While warning/blocked is visible, re-check so the banner clears after renew without reload.
    useEffect(() => {
        if (!licenseReady) return undefined;
        const needsWatch =
            (licenseStatus && licenseStatus.valid === false)
            || (licenseStatus && licenseStatus.valid && Number.isFinite(licenseStatus.days_left) && licenseStatus.days_left <= 15);
        if (!needsWatch) return undefined;
        const timer = window.setInterval(async () => {
            try {
                const ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
            } catch (_) {
                // ignore transient errors
            }
        }, 60000);
        return () => window.clearInterval(timer);
    }, [licenseReady, licenseStatus]);

    // If any API call hits 402 later (license expired while running), refresh status.
    useEffect(() => {
        const onBlocked = async () => {
            try {
                const ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
                setLicenseReady(true);
            } catch (_) {
                setLicenseStatus({ valid: false, detail: "Термін ліцензії закінчився" });
                setLicenseReady(true);
            }
        };
        window.addEventListener("licenseBlocked", onBlocked);
        return () => window.removeEventListener("licenseBlocked", onBlocked);
    }, []);

    // Load operator-controlled client portal settings (location overrides network).
    useEffect(() => {
        const loadPortalSettings = async () => {
            if (!licenseReady || isLicenseInvalid) return;
            try {
                const s = await fetchClientPortalSettings();
                setPortalSettings(s);
            } catch (_) {
                setPortalSettings(null);
            }
        };
        const loadNetworkFeatures = async () => {
            if (!licenseReady || isLicenseInvalid || !clientScopeReady) return;
            try {
                const data = await fetchClientNetworkFeatures();
                setNetworkFeatures(data?.features && typeof data.features === "object" ? data.features : {});
            } catch (_) {
                // Fail open for older backends / standalone edge cases
                setNetworkFeatures(null);
            }
        };
        loadPortalSettings();
        loadNetworkFeatures();
    }, [licenseReady, isLicenseInvalid, clientScopeReady, hasClientAccess]);

    useEffect(() => {
        const onResize = () => setShowTime(window.innerWidth >= 520);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (serverOffsetMs === null) return;
        const id = setInterval(() => setServerTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, [serverOffsetMs]);

    const formatKyiv = (d, withTime) => {
        if (!d) return "";
        const opts = {
            timeZone: "Europe/Kyiv",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
        };
        return new Intl.DateTimeFormat("uk-UA", opts).format(d).replace(",", "");
    };

    const serverNow = serverOffsetMs === null ? null : new Date(Date.now() + serverOffsetMs);
    // eslint-disable-next-line no-unused-vars
    const _serverTick = serverTick;
    const serverDateText = serverNow ? formatKyiv(serverNow, false) : "";
    const serverDateTimeText = serverNow ? formatKyiv(serverNow, true) : "";

    // Sync force-password-change flag with backend in case token is old
    useEffect(() => {
        const syncForceFlag = async () => {
            if (!licenseReady || isLicenseInvalid) return;
            try {
                const client = await fetchClient();
                const flag = !!client.force_password_change;
                setForcePasswordChange(flag);
                localStorage.setItem("forcePasswordChange", flag ? "true" : "false");
                if (flag) setSelectedSection("profile");

                // Enforce terms acceptance on every login until accepted.
                const acceptedAt = client?.terms_accepted_at;
                const needsTerms = !acceptedAt;
                setTermsRequired(needsTerms);
                if (!needsTerms) {
                    setTermsChecked(false);
                    setTermsError("");
                }
            } catch (_) {
                // ignore sync errors; UI will rely on stored flag
            }
        };
        syncForceFlag();
    }, [licenseReady, isLicenseInvalid]);

    useEffect(() => {
        if (termsRequired) setMenuOpen(false);
    }, [termsRequired]);

    const handleAcceptTerms = useCallback(async () => {
        if (!termsChecked || termsSubmitting) return;
        setTermsSubmitting(true);
        setTermsError("");
        try {
            await acceptClientTerms();
            setTermsRequired(false);
            setTermsChecked(false);
        } catch (e) {
            setTermsError(e?.message || "Не вдалося підтвердити умови");
        } finally {
            setTermsSubmitting(false);
        }
    }, [termsChecked, termsSubmitting]);

    // ✅ Check door access
    useEffect(() => {
        const checkDoorAccess = async () => {
            try {
                const sockets = await fetchSockets();
                setHasDoorAccess(sockets && sockets.length > 0);
            } catch (error) {
                setHasDoorAccess(false);
            }
        };
        if (!licenseReady || isLicenseInvalid) return;
        if (hasLocationAccess) {
            checkDoorAccess();
        }
    }, [hasLocationAccess, licenseReady, isLicenseInvalid]);

    useEffect(() => {
        const checkCameraAccess = async () => {
            try {
                const cameras = await fetchClientCameras();
                setHasCameraAccess(Array.isArray(cameras) && cameras.length > 0);
            } catch (_) {
                setHasCameraAccess(false);
            }
        };
        if (!licenseReady || isLicenseInvalid) return;
        if (hasLocationAccess) {
            checkCameraAccess();
        }
    }, [hasLocationAccess, licenseReady, isLicenseInvalid]);

    // ✅ Initialize menu
    useEffect(() => {
        if (!licenseReady) return;

        // If license is missing/expired, block the app UI but keep an informative screen.
        if (isLicenseInvalid) {
            setMenuModel({
                topItems: [],
                groups: [
                    {
                        id: "about",
                        title: "Про",
                        items: [{ name: "Про програму", key: "about", icon: "📱" }],
                    },
                ],
                bottomItems: [{ name: "Вихід", key: "logout", icon: "🚪" }],
            });
            // Keep logout in menu when profile is unavailable (license block).
            return;
        }

        const effective = portalSettings || {};
        const showNews = enabledModules.news;
        const showChat = enabledModules.chats;
        const showOperatorInfo = enabledModules.info;
        const showCars = enabledModules.cars;
        const showRequests = enabledModules.requests;
        const showPolls = enabledModules.polls;
        const showDoorAccess = enabledModules.door;
        const showCameraAccess = enabledModules.camera;
        const sensorsAllowed = enabledModules.sensors;

        const pagesFromApi = Array.isArray(effective.pages) ? effective.pages : [];
        const legacyUrl = (effective.portal_url || "").trim();
        const legacyTitle = (effective.portal_title || "Інформація").trim() || "Інформація";

        const portalPages = pagesFromApi.length
            ? pagesFromApi
                .map((p) => ({
                    page_title: (p.page_title || "Інформація").trim() || "Інформація",
                    page_url: (p.page_url || "").trim(),
                }))
                .filter((p) => p.page_url)
            : legacyUrl
                ? [{ page_title: legacyTitle, page_url: legacyUrl }]
                : [];

        const portalEnabled = !!effective.portal_enabled && portalPages.length > 0;

        // When a password reset is forced, limit navigation to profile/about/logout
        if (forcePasswordChange) {
            setMenuModel({
                topItems: [],
                groups: [
                    {
                        id: "about",
                        title: "Про",
                        items: [
                            { name: "Профіль", key: "profile", icon: "⚙️" },
                            { name: "Про програму", key: "about", icon: "📱" },
                        ],
                    },
                ],
                bottomItems: [],
            });
            return;
        }

        const topItems = [];

        const communicationItems = [];
        if (showRequests) communicationItems.push({ name: "Заявки", key: "requests", icon: "📝" });
        if (hasClientAccess && showChat) communicationItems.push({ name: "Переписки", key: "chat", icon: "💬" });
        if (hasLocationAccess && hasNetworkAccess && showCars) communicationItems.push({ name: "Авто", key: "cars", icon: "🚗" });
        if (hasClientAccess && showPolls) communicationItems.push({ name: "Опитування", key: "polls", icon: "📊" });

        const portalItems = portalEnabled
            ? portalPages.map((p, idx) => ({
                name: p.page_title,
                key: `portal:${idx}`,
                icon: "🌐",
            }))
            : [];

        const contentItems = [
            ...(hasClientAccess && showNews ? [{ name: "Новини", key: "news", icon: "📰" }] : []),
            ...(hasClientAccess && showOperatorInfo ? [{ name: "Інформація", key: "operator-info", icon: "ℹ️" }] : []),
            ...portalItems,
        ];

        const infraItems = [
            ...((sensorAccess.page_enabled || standaloneClient) && sensorsAllowed
                ? [{ name: "Сенсори", key: "sensors", icon: "🚨" }]
                : []),
            ...(hasLocationAccess && hasDoorAccess && showDoorAccess
                ? [{ name: "Відкрити замок", key: "door-access", icon: "🔑" }]
                : []),
            ...(hasLocationAccess && hasCameraAccess && showCameraAccess
                ? [{ name: "Камери", key: "camera-access", icon: "📹" }]
                : []),
        ];

        const aboutItems = [
            { name: "Профіль", key: "profile", icon: "⚙️" },
            { name: "Про програму", key: "about", icon: "📱" },
        ];

        const groups = standaloneClient
            ? [
                ...(infraItems.length ? [{ id: "infra", title: "Інфраструктура", items: infraItems }] : []),
                { id: "about", title: "Про", items: aboutItems },
            ]
            : [
                ...(communicationItems.length ? [{ id: "comm", title: "Комунікація", items: communicationItems }] : []),
                ...(contentItems.length ? [{ id: "content", title: "Контент", items: contentItems }] : []),
                ...(infraItems.length ? [{ id: "infra", title: "Інфраструктура", items: infraItems }] : []),
                { id: "about", title: "Про", items: aboutItems },
            ];

        setMenuModel({
            topItems,
            groups,
            bottomItems: [],
        });
    }, [hasClientAccess, hasLocationAccess, hasNetworkAccess, hasDoorAccess, hasCameraAccess, forcePasswordChange, portalSettings, enabledModules, licenseReady, isLicenseInvalid, sensorAccess.page_enabled, standaloneClient]);

    // If license becomes invalid while user is in the app, switch to About screen.
    useEffect(() => {
        if (isLicenseInvalid) {
            setSelectedSection("about");
        }
    }, [isLicenseInvalid]);

    // Ensure there is always an active section
    useEffect(() => {
        if (!selectedSection) {
            setSelectedSection(forcePasswordChange ? "profile" : (standaloneClient ? "sensors" : "dashboard"));
        }
    }, [selectedSection, forcePasswordChange, standaloneClient]);

    // Enforce that profile stays open until password is changed
    useEffect(() => {
        if (forcePasswordChange) {
            setSelectedSection("profile");
        }
    }, [forcePasswordChange]);

    useEffect(() => {
        if (selectedSection === "profile") {
            markClientAccessUpdatesViewed();
        }
        if (selectedSection === "requests") {
            markClientRequestUpdatesViewed();
        }
    }, [selectedSection, markClientAccessUpdatesViewed, markClientRequestUpdatesViewed]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!hasClientAccess) return;

        const cleanup = [];
        const handleNews = withInAppSound(() => refreshUnreadNews());

        networkIds.forEach((id) => {
            subscribe("news", `network_clients:${id}`, "news_new", handleNews);
            cleanup.push(() => unsubscribe("news", `network_clients:${id}`, "news_new", handleNews));
        });

        locationIds.forEach((id) => {
            subscribe("news", `location_clients:${id}`, "news_new", handleNews);
            cleanup.push(() => unsubscribe("news", `location_clients:${id}`, "news_new", handleNews));
        });

        return () => cleanup.forEach((fn) => fn());
    }, [connect, subscribe, unsubscribe, networkIds, locationIds, hasClientAccess, refreshUnreadNews, licenseReady, isLicenseInvalid]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!hasClientAccess) return;

        const cleanup = [];
        const handlePollNew = withInAppSound(() => refreshUnreadPolls());
        const handlePollVote = () => window.dispatchEvent(new Event("wr_polls_changed"));

        networkIds.forEach((id) => {
            subscribe("polls", `network_clients:${id}`, "poll_new", handlePollNew);
            subscribe("polls", `network_clients:${id}`, "poll_vote", handlePollVote);
            cleanup.push(() => unsubscribe("polls", `network_clients:${id}`, "poll_new", handlePollNew));
            cleanup.push(() => unsubscribe("polls", `network_clients:${id}`, "poll_vote", handlePollVote));
        });

        locationIds.forEach((id) => {
            subscribe("polls", `location_clients:${id}`, "poll_new", handlePollNew);
            subscribe("polls", `location_clients:${id}`, "poll_vote", handlePollVote);
            cleanup.push(() => unsubscribe("polls", `location_clients:${id}`, "poll_new", handlePollNew));
            cleanup.push(() => unsubscribe("polls", `location_clients:${id}`, "poll_vote", handlePollVote));
        });

        return () => cleanup.forEach((fn) => fn());
    }, [connect, subscribe, unsubscribe, networkIds, locationIds, hasClientAccess, refreshUnreadPolls, licenseReady, isLicenseInvalid]);

    // On load, decide whether the News badge should be visible (covers page reloads and edits).
    useEffect(() => {
        const checkUnreadNews = async () => {
            if (!licenseReady || isLicenseInvalid) return;
            if (!hasClientAccess) return;
            try {
                await refreshUnreadNews();
                return;
            } catch (_) {
                // ignore
            }

            // Fallback to local markers if backend unread check fails
            try {
                const data = await fetchNews();
                if (!data || data.length === 0) return;

                const newest = data[0];
                const newestAt = newest?.dtlm ? new Date(newest.dtlm).getTime() : null;
                const storedLastViewedAt = localStorage.getItem("lastViewedNewsAt");
                const seenAt = storedLastViewedAt ? new Date(storedLastViewedAt).getTime() : null;

                if (newestAt && seenAt && newestAt > seenAt) {
                    setUnreadNewsCount(1);
                    setHasUnreadNews(true);
                    return;
                }

                const lastSeenId = parseInt(localStorage.getItem("lastViewedNewsId"));
                if (Number.isFinite(lastSeenId) && newest?.id && newest.id > lastSeenId) {
                    setUnreadNewsCount(1);
                    setHasUnreadNews(true);
                } else {
                    setUnreadNewsCount(0);
                    setHasUnreadNews(false);
                }
            } catch (_) {
                // ignore
            }
        };
        checkUnreadNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasClientAccess, refreshUnreadNews, licenseReady, isLicenseInvalid]);

    // On load, decide whether the Polls badge should be visible (covers page reloads).
    useEffect(() => {
        refreshUnreadPolls();
    }, [refreshUnreadPolls]);

    // On load, decide whether the Chat badge should be visible (covers page reloads).
    useEffect(() => {
        refreshUnreadChats();
    }, [refreshUnreadChats]);

    // On load, decide whether the Operator Info badge should be visible (covers page reloads).
    useEffect(() => {
        refreshOperatorInfoBadge();
    }, [refreshOperatorInfoBadge]);

    useEffect(() => {
        refreshClientAccessUpdates();
    }, [refreshClientAccessUpdates]);

    useEffect(() => {
        refreshClientRequestUpdates();
    }, [refreshClientRequestUpdates]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid || !clientId) return;

        const intervalId = window.setInterval(() => {
            refreshClientRequestUpdates();
            refreshClientAccessUpdates();
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, [licenseReady, isLicenseInvalid, clientId, refreshClientRequestUpdates, refreshClientAccessUpdates]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!hasClientAccess) return;

        const cleanup = [];

        networkIds.forEach((id) => {
            const handleNetworkInfo = withInAppSound(() => {
                refreshOperatorInfoBadge();
            });

            subscribe("operator_info", `network_clients:${id}`, "operator_info_new", handleNetworkInfo);
            cleanup.push(() => unsubscribe("operator_info", `network_clients:${id}`, "operator_info_new", handleNetworkInfo));
        });

        locationIds.forEach((id) => {
            const handleLocationInfo = withInAppSound((payload) => {
                if (payload.location_id === id) {
                    refreshOperatorInfoBadge();
                }
            });

            subscribe("operator_info", `location_clients:${id}`, "operator_info_new", handleLocationInfo);
            cleanup.push(() => unsubscribe("operator_info", `location_clients:${id}`, "operator_info_new", handleLocationInfo));
        });

        return () => cleanup.forEach((fn) => fn());
    }, [connect, subscribe, unsubscribe, networkIds, locationIds, hasClientAccess, licenseReady, isLicenseInvalid, refreshOperatorInfoBadge]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;

        const roomId = `client:${clientId}`;
        const handleChatNew = () => {
            if (selectedSectionRef.current !== "chat") {
                notifyInAppSound();
            }
            refreshUnreadChats();
        };

        const handleChatHighlighted = (payload) => {
            if (payload && payload.head_id) {
                refreshUnreadChats();
            }
        };

        connect("chat", roomId);

        subscribe("chat", roomId, "chat_new", handleChatNew);

        subscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);

        return () => {
            unsubscribe("chat", roomId, "chat_new", handleChatNew);
            unsubscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);
        };

    }, [connect, subscribe, unsubscribe, clientId, licenseReady, isLicenseInvalid, refreshUnreadChats]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;

        const roomId = `client:${clientId}`;
        const handleRequestChanged = withInAppSound(() => {
            refreshClientRequestUpdates();
        });

        connect("requests", roomId);
        subscribe("requests", roomId, "request_changed", handleRequestChanged);

        return () => {
            unsubscribe("requests", roomId, "request_changed", handleRequestChanged);
        };
    }, [connect, subscribe, unsubscribe, clientId, licenseReady, isLicenseInvalid, refreshClientRequestUpdates]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;

        const roomId = `client:${clientId}`;
        const handleAccessRequestChanged = withInAppSound(() => {
            refreshClientAccessUpdates();
        });

        connect("client_access_requests", roomId);
        subscribe("client_access_requests", roomId, "client_access_request_changed", handleAccessRequestChanged);

        return () => {
            unsubscribe("client_access_requests", roomId, "client_access_request_changed", handleAccessRequestChanged);
        };
    }, [connect, subscribe, unsubscribe, clientId, licenseReady, isLicenseInvalid, refreshClientAccessUpdates]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!clientId) return;

        const roomId = `client:${clientId}`;
        const handleFeedbackUpdated = withInAppSound((payload) => {
            setHasUnreadFeedback(true);
            if (payload.id) {
                setHighlightedFeedbacks(prev => new Set([...prev, payload.id]));
                // Keep highlight visible for 60 seconds
                setTimeout(() => {
                    setHighlightedFeedbacks(prev => {
                        const updated = new Set(prev);
                        updated.delete(payload.id);
                        return updated;
                    });
                }, 60000);
            }
        });

        connect("feedbacks", roomId);
        subscribe("feedbacks", roomId, "feedback_updated", handleFeedbackUpdated);

        return () => {
            unsubscribe("feedbacks", roomId, "feedback_updated", handleFeedbackUpdated);
        };

    }, [connect, subscribe, unsubscribe, clientId, licenseReady, isLicenseInvalid]);

    // ✅ Handle menu clicks
    const handleSectionClick = useCallback(async (key) => {
        if (termsRequired && key !== "logout") {
            setMessage("Для продовження потрібно погодитись з умовами або вийти.");
            setMenuOpen(false);
            setOpenGroupId(null);
            return;
        }
        setMenuOpen(false);
        setOpenGroupId(null);

        if (isLicenseInvalid && key !== "logout") {
            setSelectedSection("about");
            setMessage("Роботу заблоковано через завершення терміну ліцензії. Зверніться до адміністратора.");
            return;
        }

        if (forcePasswordChange && !["profile", "about", "logout"].includes(key)) {
            setSelectedSection("profile");
            setMessage("Для доступу до інших розділів спочатку змініть пароль у профілі.");
            return;
        }

        if (standaloneClient && !["profile", "about", "sensors", "logout"].includes(key)) {
            setSelectedSection("sensors");
            setMessage("Повний функціонал відкриється після підключення акаунта до мережі. Зараз доступні профіль, сенсори та інформація про програму.");
            return;
        }

        // Feedbacks are available only for clients attached to a location/network.
        if (key === "feedbacks" && (!hasClientAccess || !feedbacksEnabled)) {
            setSelectedSection("about");
            setMessage(!feedbacksEnabled ? "Відгуки вимкнені оператором." : "Відгуки доступні лише після прив'язки акаунта до локації.");
            return;
        }

        if (key === "requests" && !requestsEnabled) {
            setSelectedSection("about");
            setMessage("Заявки вимкнені оператором.");
            return;
        }

        if (key === "polls" && (!hasClientAccess || !pollsEnabled)) {
            setSelectedSection("about");
            setMessage(!hasClientAccess ? "Опитування доступні лише після прив'язки акаунта до сервісу або локації." : "Опитування вимкнені оператором.");
            return;
        }

        if (key === "cars") {
            if (!hasLocationAccess || !hasNetworkAccess) {
                setSelectedSection("about");
                setMessage("Авто доступне лише після прив'язки акаунта до локації.");
                return;
            }
            if (!carsEnabled) {
                setSelectedSection("about");
                setMessage("Авто вимкнене оператором.");
                return;
            }
        }

        if (key === "door-access") {
            if (!hasLocationAccess || !hasDoorAccess) {
                setSelectedSection("about");
                setMessage("Відкривання дверей доступне лише для прив'язаних мешканців локації.");
                return;
            }
            if (!enabledModules.door) {
                setSelectedSection("about");
                setMessage("Доступ до відкривання дверей вимкнений.");
                return;
            }
        }

        if (key === "camera-access") {
            if (!hasLocationAccess || !hasCameraAccess) {
                setSelectedSection("about");
                setMessage("Камери доступні лише для прив'язаних мешканців локації.");
                return;
            }
            if (!enabledModules.camera) {
                setSelectedSection("about");
                setMessage("Доступ до камер вимкнений.");
                return;
            }
        }

        if (key === "logout") {
            try {
                await logout();
            } catch (_) {
                // ignore
            }
            navigate("/");
            return;
        }

        if (!clientId || isNaN(clientId)) {
            setMessage("❌ Не знайдено client_id.");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            if (key === "news") {
                setSelectedSection(key);
            } else if (key === "operator-info") {
                setSelectedSection(key);
            } else if (key === "chat") {
                setSelectedSection(key);
            } else if (key === "profile") {
                setSelectedSection(key);
                markClientAccessUpdatesViewed();
            } else if (key === "requests") {
                setSelectedSection(key);
                markClientRequestUpdatesViewed();
            } else if (key === "polls") {
                setSelectedSection(key);
            } else if (key === "feedbacks") {
                setSelectedSection(key);
                setHasUnreadFeedback(false);
                localStorage.setItem("lastViewedFeedbackAt", new Date().toISOString());
            } else if (key === "about") {
                setSelectedSection(key);
                setHasUnreadFeedback(false);
            } else {
                setSelectedSection(key);
            }
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [clientId, navigate, forcePasswordChange, hasClientAccess, hasLocationAccess, hasNetworkAccess, hasDoorAccess, hasCameraAccess, feedbacksEnabled, isLicenseInvalid, termsRequired, requestsEnabled, pollsEnabled, carsEnabled, enabledModules, markClientAccessUpdatesViewed, markClientRequestUpdatesViewed, standaloneClient]);

    const handlePasswordChanged = (flag) => {
        const normalized = !!flag;
        const wasForced = !!forcePasswordChange;
        setForcePasswordChange(normalized);
        localStorage.setItem("forcePasswordChange", normalized ? "true" : "false");
        // Only auto-navigate away when the app was previously enforcing a password change
        // and the user has just resolved it.
        if (wasForced && !normalized) {
            setSelectedSection(standaloneClient ? "sensors" : "dashboard");
        }
    };

    // ===== Handle feedback opening =====
    useEffect(() => {
        const handleOpenFeedback = () => {
            handleSectionClick("feedbacks");
        };

        window.addEventListener("openFeedback", handleOpenFeedback);
        return () => window.removeEventListener("openFeedback", handleOpenFeedback);
    }, [handleSectionClick]);

    const consumePendingPushOpen = useCallback(() => {
        if (isLicenseInvalid) return;

        let raw = null;
        try {
            raw = localStorage.getItem("WR_PENDING_PUSH_OPEN");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        let parsed = null;
        try {
            parsed = JSON.parse(raw);
        } catch (_) {
            try {
                localStorage.removeItem("WR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
            return;
        }

        const kind = (parsed?.kind || "").toString().trim();
        if (!kind) {
            try {
                localStorage.removeItem("WR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
            return;
        }

        const openSection = (section) => {
            setMenuOpen(false);
            setOpenGroupId(null);
            setSelectedSection(section);
            return true;
        };

        let opened = false;

        if (kind === "chat") {
            applyPendingPushItemIds(parsed);
            opened = openSection("chat");
        } else if (kind === "news") {
            applyPendingPushItemIds(parsed);
            opened = openSection("news");
        } else if (kind === "sensor_alert") {
            applyPendingPushItemIds(parsed);
            opened = openSection("sensors");
        } else if (kind === "operator_info") {
            applyPendingPushItemIds(parsed);
            opened = openSection("operator-info");
        } else if (kind === "poll") {
            applyPendingPushItemIds(parsed);
            opened = openSection("polls");
        } else if (["request", "request_response", "request_reply"].includes(kind)) {
            applyPendingPushItemIds(parsed);
            opened = openSection("requests");
        } else if (["feedback", "feedback_response", "feedback_reply"].includes(kind)) {
            applyPendingPushItemIds(parsed);
            opened = openSection("feedbacks");
        } else if (["access_request", "client_access_request"].includes(kind)) {
            applyPendingPushItemIds(parsed);
            opened = openSection("profile");
        }

        if (opened) {
            try {
                localStorage.removeItem("WR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
        }
    }, [isLicenseInvalid]);

    useEffect(() => {
        if (!licenseReady) return;
        consumePendingPushOpen();
        const handler = () => consumePendingPushOpen();
        window.addEventListener("wrPushOpen", handler);
        return () => window.removeEventListener("wrPushOpen", handler);
    }, [consumePendingPushOpen, licenseReady]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem("WR_PENDING_PUSH_OPEN");
            if (!raw) return;
            applyPendingPushItemIds(JSON.parse(raw));
        } catch (_) {
            // ignore
        }
    }, []);

    const flattenedMenuItems = useMemo(() => ([
        ...(Array.isArray(menuModel.topItems) ? menuModel.topItems : []),
        ...(Array.isArray(menuModel.groups) ? menuModel.groups.flatMap((group) => Array.isArray(group?.items) ? group.items : []) : []),
        ...(Array.isArray(menuModel.bottomItems) ? menuModel.bottomItems : []),
    ]), [menuModel]);

    useEffect(() => {
        let hasPendingPush = false;
        try {
            hasPendingPush = Boolean(localStorage.getItem("WR_PENDING_PUSH_OPEN"));
        } catch (_) {
            hasPendingPush = false;
        }
        if (hasPendingPush) return;

        const allowedKeys = new Set(flattenedMenuItems.map((item) => item?.key).filter(Boolean));
        allowedKeys.add("feedbacks");
        // Home / widgets lives outside the drawer menu — never treat as "invalid".
        allowedKeys.add("dashboard");
        if (!selectedSection || !allowedKeys.size || allowedKeys.has(selectedSection)) {
            return;
        }
        if (allowedKeys.has("dashboard")) {
            setSelectedSection("dashboard");
            return;
        }
        if (allowedKeys.has("sensors")) {
            setSelectedSection("sensors");
            return;
        }
        if (allowedKeys.has("profile")) {
            setSelectedSection("profile");
            return;
        }
        if (allowedKeys.has("about")) {
            setSelectedSection("about");
        }
    }, [flattenedMenuItems, selectedSection]);

    const homeSection = standaloneClient ? "sensors" : "dashboard";

    const goHome = useCallback(() => {
        handleSectionClick(homeSection);
    }, [handleSectionClick, homeSection]);

    useEffect(() => {
        return pushAndroidBackHandler(() => {
            if (chatShellNav?.focused) {
                chatShellNav.back?.();
                return true;
            }
            if (menuOpen) {
                setMenuOpen(false);
                return true;
            }
            if (openGroupId) {
                setOpenGroupId(null);
                return true;
            }
            if (selectedSection !== homeSection) {
                handleSectionClick(homeSection);
                return true;
            }
            return false;
        });
    }, [chatShellNav, menuOpen, openGroupId, selectedSection, homeSection, handleSectionClick]);

    useEffect(() => {
        selectedSectionRef.current = selectedSection;
    }, [selectedSection]);

    useEffect(() => {
        if (selectedSection !== "chat") {
            setChatShellNav(null);
        }
    }, [selectedSection]);

    const currentPageTitle = (() => {
        if (!selectedSection) return null;
        if (selectedSection === "dashboard") return null;
        if (selectedSection === "sensors") return "Сенсори";
        if (selectedSection.startsWith("portal:")) {
            const pageId = selectedSection.split(":")[1];
            const pages = Array.isArray(portalSettings?.pages) ? portalSettings.pages : [];
            const portalPage = pages.find((page) => String(page?.id) === pageId);
            return portalPage?.title || "Портал";
        }
        if (selectedSection === "portal") {
            return (portalSettings?.portal_title || "Портал").trim() || "Портал";
        }
        if (selectedSection === "feedbacks") return "Залишити відгук";
        return flattenedMenuItems.find((item) => item?.key === selectedSection)?.name || null;
    })();

    const displayPageTitle = chatShellNav?.focused
        ? chatShellNav.title
        : currentPageTitle;

    return (
        <>
            {termsRequired && (
                <div className="modal-backdrop" role="dialog" aria-modal="true">
                    <div className="modal-content terms-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Умови використання</h3>
                        <div className="terms-modal__text">
                            <p className="terms-modal__p terms-modal__p--first">
                                Щоб користуватись додатком, потрібно підтвердити умови.
                            </p>
                            <p className="terms-modal__p">
                                Ми зберігаємо лише необхідні дані для роботи сервісу (наприклад ім’я, email,
                                налаштування акаунта та технічні події). Номер телефону для реєстрації не вимагається.
                            </p>
                        </div>

                        <label className="terms-modal__agree">
                            <input
                                type="checkbox"
                                checked={termsChecked}
                                onChange={(e) => setTermsChecked(e.target.checked)}
                                className="terms-modal__checkbox"
                            />
                            <span className="terms-modal__agreeText">Я погоджуюсь з умовами використання та політикою конфіденційності.</span>
                        </label>

                        {termsError && <div className="terms-modal__error">{termsError}</div>}

                        <div className="modal-actions terms-modal__actions">
                            <button
                                className="add-btn"
                                onClick={handleAcceptTerms}
                                disabled={!termsChecked || termsSubmitting}
                            >
                                {termsSubmitting ? "Збереження..." : "Погоджуюсь"}
                            </button>
                            <button
                                type="button"
                                className="add-btn terms-modal__decline"
                                onClick={() => handleSectionClick("logout")}
                                disabled={termsSubmitting}
                            >
                                Не погоджуюсь
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className={`header-bar ${chatShellNav?.focused ? "header-bar--subview" : ""}`}>
                <NetworkLogoBadge className="network-logo-badge--corner" />
                <div className="header-left">
                    <div className="header-greeting">
                        <div className="header-greeting-top">
                            <button
                                type="button"
                                className="header-home-btn"
                                onClick={goHome}
                                aria-label="На головну"
                                title="На головну"
                            >
                                <Home size={17} strokeWidth={2.4} aria-hidden="true" />
                            </button>
                            <div className="header-greeting-line">
                                {clientName ? (
                                    <>Вітаю, <span>{clientName}</span>!</>
                                ) : (
                                    <>Вітаю, клієнте!</>
                                )}
                            </div>
                        </div>

                        {weatherText ? (
                            <div className="header-weather-row">
                                {serverNow ? `${showTime ? serverDateTimeText : serverDateText}  ` : ""}
                                {weatherText}
                            </div>
                        ) : null}

                        {(displayPageTitle || (!weatherText && serverNow)) && (
                            <div className="header-meta-row">
                                {displayPageTitle ? (
                                    <div className="header-page-title" title={displayPageTitle}>
                                        <span className="header-page-title__text">{displayPageTitle}</span>
                                    </div>
                                ) : null}

                                {!weatherText && serverNow ? (
                                    <div className="header-datetime">
                                        {showTime ? serverDateTimeText : serverDateText}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>

                <div className="navbar-container" ref={navbarRef}>
                    {menuModel.topItems.map(({ key, name, icon }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleSectionClick(key)}
                            className={`nav-item ${selectedSection === key ? "active" : ""}`}
                        >
                            <span className="nav-icon">{icon}</span>
                            <span className="nav-text">{name}</span>
                        </button>
                    ))}

                    {menuModel.groups.map((group) => {
                        const isOpen = openGroupId === group.id;
                        const hasGroupDot =
                            (group.id === "comm" && (hasRequestUpdates || hasUnreadPolls || hasChats)) ||
                            (group.id === "content" && (hasUnreadNews || hasOperatorInfo)) ||
                            (group.id === "about" && ((feedbacksEnabled && hasUnreadFeedback) || hasAccessRequestUpdates));

                        return (
                            <div className="nav-group" key={group.id}>
                                <button
                                    type="button"
                                    className={`nav-group-button ${isOpen ? "open" : ""}`}
                                    onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                    aria-expanded={isOpen}
                                >
                                    <span className="nav-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                    <span className="nav-group-text">{group.title}</span>
                                    {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                </button>

                                {isOpen && (
                                    <div className="nav-group-panel" role="menu">
                                        {group.items.map(({ key, name, icon }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => handleSectionClick(key)}
                                                className={`nav-panel-button ${selectedSection === key ? "active" : ""}`}
                                                role="menuitem"
                                            >
                                                <span className="nav-icon">{icon}</span>
                                                <span className="nav-text">{name}</span>
                                                {getMenuBadgeCount(key) > 0 ? (
                                                    <span className="nav-badge">{getMenuBadgeCount(key)}</span>
                                                ) : (key === "requests" && hasRequestUpdates) ||
                                                    (key === "profile" && hasAccessRequestUpdates) ||
                                                    (feedbacksEnabled && key === "about" && hasUnreadFeedback) ||
                                                    (feedbacksEnabled && key === "feedbacks" && hasUnreadFeedback) ? (
                                                    <span className="nav-dot" />
                                                ) : null}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {menuModel.bottomItems.map(({ key, name, icon }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleSectionClick(key)}
                            className="nav-item nav-item--single"
                        >
                            <span className="nav-icon">{icon}</span>
                            <span className="nav-text">{name}</span>
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className={`header-hamburger ${menuOpen ? "is-open" : ""}`}
                    onClick={toggleMenu}
                    aria-label="Меню"
                    aria-expanded={menuOpen}
                >
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                </button>
            </div>

            <div className={`sidebar-drawer ${menuOpen ? "open" : ""}`} ref={drawerRef}>
                {menuMounted ? (
                    <>
                <div className="drawer-head">
                    <div className="drawer-head__copy">
                        <strong className="drawer-head__title">Меню</strong>
                        <span className="drawer-head__subtitle">Розділи клієнта</span>
                    </div>
                    <div className="drawer-head__theme">
                        {menuOpen ? <ThemeSelector compact /> : null}
                    </div>
                </div>
                <div className="drawer-scroll">
                    <div className="drawer-menu">
                        {menuModel.topItems.map(({ key, name, icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleSectionClick(key)}
                                className={`drawer-button ${selectedSection === key ? "active" : ""}`}
                            >
                                <span className="nav-icon">{icon}</span>
                                <span className="nav-text">{name}</span>
                            </button>
                        ))}

                        {menuModel.groups.map((group) => {
                            const isOpen = openGroupId === group.id;
                            const hasGroupDot =
                                (group.id === "comm" && (hasRequestUpdates || hasUnreadPolls || hasChats)) ||
                                (group.id === "content" && (hasUnreadNews || hasOperatorInfo)) ||
                                (group.id === "about" && ((feedbacksEnabled && hasUnreadFeedback) || hasAccessRequestUpdates));

                            return (
                                <div className="drawer-group" key={group.id}>
                                    <button
                                        type="button"
                                        className={`drawer-group-button ${isOpen ? "open" : ""}`}
                                        onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="drawer-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                        <span className="drawer-group-text">{group.title}</span>
                                        {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                    </button>

                                    {isOpen && (
                                        <div className="drawer-group-items">
                                            {group.items.map(({ key, name, icon }) => {
                                                const badge = getMenuBadgeCount(key);
                                                return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => handleSectionClick(key)}
                                                    className={`drawer-button ${selectedSection === key ? "active" : ""}`}
                                                >
                                                    <span className="nav-icon">{icon}</span>
                                                    <span className="nav-text">{name}</span>
                                                    {badge > 0 && <span className="nav-badge">{badge}</span>}
                                                    {badge <= 0 && key === "profile" && hasAccessRequestUpdates && <span className="nav-dot" />}
                                                    {badge <= 0 && key === "requests" && hasRequestUpdates && <span className="nav-dot" />}
                                                    {badge <= 0 && feedbacksEnabled && key === "about" && hasUnreadFeedback && <span className="nav-dot" />}
                                                </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {menuModel.bottomItems.map(({ key, name, icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleSectionClick(key)}
                                className={`drawer-button ${key === "logout" ? "drawer-button--logout" : ""}`}
                            >
                                <span className="nav-icon">{icon}</span>
                                <span className="nav-text">{name}</span>
                            </button>
                        ))}
                    </div>
                </div>
                    </>
                ) : null}
            </div>
            {menuOpen && <div className="drawer-backdrop" onClick={closeMenu} />}

            <div className={`shell-bottom-dock ${menuOpen ? "is-menu-open" : ""}`} aria-label="Навігація">
                <button
                    type="button"
                    className="shell-bottom-dock__btn"
                    onClick={goHome}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label="На головну"
                    title="На головну"
                >
                    <Home size={18} strokeWidth={2.4} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className={`shell-bottom-dock__btn shell-bottom-dock__btn--menu ${menuOpen ? "is-open" : ""}`}
                    onClick={toggleMenu}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label={menuOpen ? "Закрити меню" : "Меню"}
                    aria-expanded={menuOpen}
                    title={menuOpen ? "Закрити меню" : "Меню"}
                >
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                    <span className="header-hamburger__line" aria-hidden="true" />
                </button>
            </div>

            {/* Main content */}
            <div
                className="main-content"
                onClick={() => menuOpen && closeMenu()}
            >
                {removedNetworkBanner && selectedSection !== "profile" && (
                    <div className="message-banner license-warning" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
                        <div className="pending-approvals-banner__content">
                            <div>
                                <strong>Вас видалено з мережі «{removedNetworkBanner.networkName}»</strong>
                                <div style={{ marginTop: 4, fontSize: 13 }}>
                                    Доступні приватні сенсори та приєднання за інвайт-кодом у профілі.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="add-btn"
                                onClick={async () => {
                                    try {
                                        await dismissRemovedNetworkBanner();
                                    } catch (_) {
                                        // ignore
                                    }
                                    setRemovedNetworkBanner(null);
                                    handleSectionClick("profile");
                                }}
                            >
                                Відкрити профіль
                            </button>
                        </div>
                    </div>
                )}

                {hasAccessRequestUpdates && selectedSection !== "profile" && (
                    <div className="message-banner pending-approvals-banner" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
                        <div className="pending-approvals-banner__content">
                            <div>
                                <strong>Є оновлення по доступу до мережі</strong>
                                <div style={{ marginTop: 4, fontSize: 13 }}>
                                    Оператор міг підключити нову мережу або залишити уточнення у ваших запитах.
                                </div>
                            </div>
                            <button type="button" className="add-btn" onClick={() => handleSectionClick("profile")}>
                                Відкрити профіль
                            </button>
                        </div>
                    </div>
                )}

                {showLicenseWarning && (
                    <div className="message-banner license-warning license-warning--sticky" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
                        Увага! Термін ліцензії закінчується через <strong>{warningDaysLeft}</strong>{" "}
                        {warningDaysLeft === 1 ? "день" : warningDaysLeft < 5 ? "дні" : "днів"}.
                        Зверніться до адміністратора мережі для продовження. Це повідомлення зникне після продовження ліцензії.
                    </div>
                )}

                {isLicenseInvalid && (
                    <div className="message-banner license-blocked license-warning--sticky" role="alert" aria-live="assertive" style={{ marginBottom: 12 }}>
                        <strong>Роботу заблоковано.</strong> {licenseStatus?.detail || "Термін ліцензії закінчився або ліцензія не встановлена."}
                        <div style={{ marginTop: 8 }}>
                            Зверніться до адміністратора мережі для продовження. Доступ відновиться після оновлення ліцензії.
                        </div>
                    </div>
                )}

                {forcePasswordChange && (
                    <div
                        className="message-banner password-change-banner"
                        role="alert"
                        aria-live="assertive"
                        style={{ marginBottom: 12 }}
                    >
                        <strong className="password-change-banner__title">Увага!</strong>
                        <div className="password-change-banner__text">
                            Пароль вашого акаунта було змінено оператором. Для продовження роботи оновіть пароль у розділі "Профіль".
                        </div>
                    </div>
                )}
                {loading && <p>⏳ Виконується запит...</p>}
                {message && <p>{message}</p>}

                {selectedSection === "dashboard" && (
                    <ClientWidgets
                        onNavigate={handleSectionClick}
                        hasUnreadNews={hasUnreadNews}
                        unreadNewsCount={unreadNewsCount}
                        hasUnreadPolls={hasUnreadPolls}
                        unreadPollsCount={unreadPollsCount}
                        hasOperatorInfo={hasOperatorInfo}
                        unreadOperatorInfoCount={unreadOperatorInfoCount}
                        hasChats={hasChats}
                        unreadChatsCount={unreadChatsCount}
                        hasRequestUpdates={hasRequestUpdates}
                        sensorAccess={sensorAccess}
                        enabledModules={enabledModules}
                    />
                )}
                {selectedSection === "requests" && <ClientRequests />}
                {selectedSection === "chat" && <ClientChat
                    activeNetworkId={activeNetworkId}
                    onUnreadChatsCountChange={(count) => {
                        setUnreadChatsCount(count);
                        setHasChats(count > 0);
                    }}
                    onShellNavChange={setChatShellNav}
                />}
                {selectedSection === "cars" && <ClientCars onNavigate={handleSectionClick} hasNetworkAccess={hasNetworkAccess} activeNetworkId={activeNetworkId} />}
                {selectedSection === "news" && (
                    <News
                        onNewsViewed={() => {
                            setHasUnreadNews(false);
                            setUnreadNewsCount(0);
                            refreshUnreadNews();
                        }}
                    />
                )}
                {selectedSection === "polls" && (
                    <ClientPolls
                        onPollsViewed={() => {
                            setHasUnreadPolls(false);
                            setUnreadPollsCount(0);
                            refreshUnreadPolls();
                        }}
                        activeLocationId={activeLocationId}
                        hasLocationAccess={hasLocationAccess}
                    />
                )}
                {selectedSection === "sensors" && <ClientSensors access={sensorAccess} />}
                {selectedSection === "door-access" && <DoorAccess />}
                {selectedSection === "camera-access" && <CameraAccess />}
                {(selectedSection === "portal" || selectedSection.startsWith("portal:")) && (() => {
                    const effective = portalSettings || {};
                    const pagesFromApi = Array.isArray(effective.pages) ? effective.pages : [];
                    const legacyUrl = (effective.portal_url || "").trim();
                    const legacyTitle = (effective.portal_title || "Інформація").trim() || "Інформація";

                    const portalPages = pagesFromApi.length
                        ? pagesFromApi
                            .map((p) => ({
                                page_title: (p.page_title || "Інформація").trim() || "Інформація",
                                page_url: (p.page_url || "").trim(),
                            }))
                            .filter((p) => p.page_url)
                        : legacyUrl
                            ? [{ page_title: legacyTitle, page_url: legacyUrl }]
                            : [];

                    const enabled = !!effective.portal_enabled && portalPages.length > 0;

                    let idx = 0;
                    if (selectedSection.startsWith("portal:")) {
                        const parsed = parseInt(selectedSection.split(":")[1]);
                        idx = Number.isFinite(parsed) ? parsed : 0;
                    }
                    const page = portalPages[idx] || portalPages[0] || null;
                    return <ClientPortal enabled={enabled} page={page} />;
                })()}
                {selectedSection === "operator-info" && (
                    <ClientOperatorInfo
                        onOperatorInfoViewed={() => {
                            setHasOperatorInfo(false);
                            setUnreadOperatorInfoCount(0);
                            refreshOperatorInfoBadge();
                        }}
                    />
                )}
                {selectedSection === "profile" && <ClientProfile onPasswordChanged={handlePasswordChanged} />}
                {selectedSection === "about" && (
                    <div>
                        <AboutClients
                            onNavigate={handleSectionClick}
                            hasUnreadFeedback={hasUnreadFeedback}
                            highlightedFeedbacks={highlightedFeedbacks}
                            feedbacksEnabled={portalSettings?.show_feedbacks !== false}
                            hasClientAccess={hasClientAccess}
                            showLogout={isLicenseInvalid}
                        />
                        {hasUnreadFeedback && (
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <span style={{ color: "#dc3545", fontSize: "20px" }}>●</span>
                            </div>
                        )}
                    </div>
                )}
                {selectedSection === "feedbacks" && <Feedbacks highlightedFeedbacks={highlightedFeedbacks} setHighlightedFeedbacks={setHighlightedFeedbacks} hasClientAccess={hasClientAccess} />}
            </div>
        </>
    );
};

export default ClientPage;
